import React, { useCallback, useMemo } from 'react';
import { Grid, Stack, Typography } from 'components/ui';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { InspectionLink } from 'utils/link';
import { IndexListItemButton } from 'components/styles_components/list';
import Inspection from 'utils/inspection';
import State from 'components/inspection/state';
import Date from 'components/date';
import Result from 'components/inspection/result';
import useMobile from 'hooks/use_mobile';

const ListItem = ({ inspection }) => {
  const navigate = useNavigate();
  const isMobile = useMobile();

  const inspectionFactoryInstance = useMemo(() => Inspection(inspection), [inspection]);

  const handleClick = useCallback(() => {
    navigate(InspectionLink(inspection.id));
  }, [navigate, inspection.id]);

  const mobileContent = useMemo(
    () => (
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Stack>
            <Typography variant="button">{inspection.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage id="inspection.number" />: {inspection.number}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage id="place" />: {inspection.place.name}
            </Typography>
            {inspection.inspectionSubject && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FormattedMessage id="inspection_subject" />: {inspection.inspectionSubject.name}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6 }} textAlign="left">
          <State status={inspection.status} />
        </Grid>
        <Grid size={{ xs: 6 }} textAlign="right">
          <Date
            date={inspection.dueDate}
            warning={inspection.incoming}
            error={inspection.overdue}
          />
        </Grid>
        {inspectionFactoryInstance.done && (
          <>
            <Grid size={{ xs: 6 }} textAlign="left">
              <Date date={inspection.doneAt} />
            </Grid>
            <Grid size={{ xs: 6 }} textAlign="right">
              <Result result={inspection.result} />
            </Grid>
          </>
        )}
      </Grid>
    ),
    [inspection, inspectionFactoryInstance.done]
  );

  const desktopContent = useMemo(
    () => (
      <Grid container justifyContent="center" alignItems="center" spacing={2}>
        <Grid size={{ xs: 4 }}>
          <Stack>
            <Typography variant="button">{inspection.name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage id="inspection.number" />: {inspection.number}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage id="place" />: {inspection.place.name}
            </Typography>
            {inspection.inspectionSubject && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FormattedMessage id="inspection_subject" />: {inspection.inspectionSubject.name}
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 2 }} textAlign="center">
          <State status={inspection.status} />
        </Grid>
        <Grid size={{ xs: 2 }} textAlign="center">
          <Date
            date={inspection.dueDate}
            warning={inspection.incoming}
            error={inspection.overdue}
          />
        </Grid>
        <Grid size={{ xs: 2 }} textAlign="center">
          <Date date={inspection.doneAt} />
        </Grid>
        <Grid size={{ xs: 2 }} textAlign="center">
          <Result result={inspection.result} />
        </Grid>
      </Grid>
    ),
    [inspection]
  );

  return (
    <IndexListItemButton className={inspectionFactoryInstance.htmlClass} onClick={handleClick}>
      {isMobile ? mobileContent : desktopContent}
    </IndexListItemButton>
  );
};

export default React.memo(ListItem);
